import React, { useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Trophy, Plus, Trash2, Loader2 } from 'lucide-react';

const emptyQ = () => ({ prompt: '', options: ['', '', '', ''], answer_index: 0 });

/**
 * OlympiadBuilder — admin/teacher UI to create a weekly Math Olympiad challenge.
 * Uses the existing `POST /math-olympiad/challenges` endpoint. Auto-deactivates prior.
 */
export const OlympiadBuilder = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState('Weekly Math Olympiad');
  const [theme, setTheme] = useState('Mixed Concepts');
  const [gradeBand, setGradeBand] = useState('all');
  const [duration, setDuration] = useState(600);
  const [questions, setQuestions] = useState([emptyQ()]);
  const [submitting, setSubmitting] = useState(false);

  const updateQ = (idx, patch) => {
    setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };
  const updateOption = (qi, oi, val) => {
    setQuestions((qs) => qs.map((q, i) => {
      if (i !== qi) return q;
      const opts = [...q.options];
      opts[oi] = val;
      return { ...q, options: opts };
    }));
  };

  const canSubmit = () => title.trim() && questions.length > 0 &&
    questions.every((q) => q.prompt.trim() && q.options.every((o) => o.trim()));

  const submit = async () => {
    if (!canSubmit()) { toast.error('Fill all questions and options'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/math-olympiad/challenges', {
        title, theme, grade_band: gradeBand, duration_seconds: Number(duration),
        questions, activate: true,
      });
      toast.success(`Challenge created — ${res.data?.title}`);
      onCreated?.(res.data);
      onClose?.();
      setQuestions([emptyQ()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="olympiad-builder">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" /> New Math Olympiad Challenge
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="oly-title">Title</Label>
              <Input id="oly-title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="oly-title" />
            </div>
            <div>
              <Label htmlFor="oly-theme">Theme</Label>
              <Input id="oly-theme" value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="oly-theme" />
            </div>
            <div>
              <Label htmlFor="oly-band">Grade Band</Label>
              <Input id="oly-band" value={gradeBand} onChange={(e) => setGradeBand(e.target.value)} placeholder="e.g. 3-5" data-testid="oly-band" />
            </div>
            <div>
              <Label htmlFor="oly-duration">Duration (sec)</Label>
              <Input id="oly-duration" type="number" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="oly-duration" />
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4 space-y-3">
            {questions.map((q, qi) => (
              <div key={qi} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`oly-q-${qi}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-extrabold text-slate-500 uppercase">Question {qi + 1}</span>
                  {questions.length > 1 && (
                    <button
                      className="text-red-500 hover:text-red-700"
                      onClick={() => setQuestions((qs) => qs.filter((_, i) => i !== qi))}
                      data-testid={`oly-q-del-${qi}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Textarea
                  value={q.prompt}
                  onChange={(e) => updateQ(qi, { prompt: e.target.value })}
                  placeholder="Question prompt (e.g. What is 12 × 12?)"
                  rows={2}
                  data-testid={`oly-q-prompt-${qi}`}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {q.options.map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={`ans-${qi}`}
                        checked={q.answer_index === oi}
                        onChange={() => updateQ(qi, { answer_index: oi })}
                        data-testid={`oly-q-ans-${qi}-${oi}`}
                        className="w-4 h-4 text-amber-500"
                      />
                      <Input
                        value={opt}
                        onChange={(e) => updateOption(qi, oi, e.target.value)}
                        placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                        className="flex-1"
                        data-testid={`oly-q-opt-${qi}-${oi}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() => setQuestions((qs) => [...qs, emptyQ()])}
              className="w-full rounded-lg"
              data-testid="oly-add-q"
            >
              <Plus className="w-4 h-4 mr-1" /> Add another question
            </Button>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="oly-cancel">Cancel</Button>
            <Button
              onClick={submit}
              disabled={submitting || !canSubmit()}
              className="text-white font-extrabold"
              style={{ background: 'linear-gradient(135deg,#F59E0B,#DC2626)' }}
              data-testid="oly-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Publish Challenge'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OlympiadBuilder;
